// ===========================================
// SHIFT STATE STORE
// ===========================================

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { ChecklistItem, getRequiredChecklistItems } from '../config/roleShiftConfig';
import { ShiftType } from '../config/shiftTypeConfig';

// Types
interface Section {
  id: string;
  name: string;
  color: string;
}

interface Table {
  id: string;
  number: string;
  capacity: number;
}

interface MenuBriefingItem {
  id: string;
  name: string;
  description?: string;
  priceCents?: number;
  regularPriceCents?: number;
  availableQuantity?: number;
  staffDescription?: string;
  allergenInfo?: string;
  ingredientsList?: string;
  pairingSuggestions?: string[];
  prepNotes?: string;
}

interface ShiftChecklist {
  // Server/Bartender checklist items
  menuReviewed: boolean;
  allergiesReviewed: boolean;
  sectionConfirmed: boolean;
  // Host checklist items
  reservationsReviewed: boolean;
  // Cook checklist items
  eightySixReviewed: boolean;
  stationConfirmed: boolean;
  // Manager checklist items
  overviewReviewed: boolean;
}

interface ShiftStats {
  tablesServed: number;
  guestsServed: number;
  salesTotalCents: number;
  tipsTotalCents: number;
}

interface PreShiftData {
  employee: {
    id: string;
    firstName: string;
    lastName: string;
    primaryRole: string;
  };
  shiftInstance: {
    id: string;
    name: string;
    date: string;
    startTime: string;
    endTime: string;
    status: string;
  } | null;
  shiftAssignment: {
    id: string;
    shiftInstanceId: string;
    role: string;
    status: string;
    station?: string | null;
    sectionId?: string | null;
    scheduledStart: string;
    scheduledEnd: string;
    actualStart?: string | null;
    actualEnd?: string | null;
    notes?: string | null;
  } | null;
  assignedSection: {
    id: string;
    name: string;
    color: string;
    tables: Table[];
  } | null;
  availableSections: {
    id: string;
    name: string;
    color: string;
    currentServerId: string | null;
    tableCount: number;
    tables?: Table[];
  }[];
  menu: {
    dailySpecials: MenuBriefingItem[];
    eightySixItems: MenuBriefingItem[];
    limitedItems: MenuBriefingItem[];
  };
  announcements: {
    id: string;
    title: string;
    message: string;
    priority: string;
    createdAt?: string;
  }[];
  guestAlerts: {
    reservationId: string;
    guestName: string;
    time: string;
    partySize: number;
    allergies: string[];
    dietaryNotes?: string;
    isVIP: boolean;
    specialRequests?: string;
  }[];
  vipGuests: {
    reservationId: string;
    guestName: string;
    time: string;
    partySize: number;
    preferences?: Record<string, any>;
    notes?: string;
  }[];
  reservations: {
    id: string;
    guestName: string;
    time: string;
    partySize: number;
    status: string;
    specialRequests?: string;
    tableIds?: string[];
    phone?: string;
    isVIP?: boolean;
    allergies?: string[];
  }[];
  // Manager-only fields
  activeStaff?: {
    employeeId: string;
    name: string;
    role: string;
    clockInTime: string;
    sectionId?: string;
  }[];
  staffOverview?: {
    totalActive: number;
    byRole: Record<string, number>;
  };
}

type ShiftStatus = 'no_shift' | 'selecting_shift_type' | 'pre_shift' | 'active' | 'post_shift';

interface ShiftState {
  // Core shift state
  status: ShiftStatus;
  timeEntryId: string | null;
  shiftStartTime: Date | null;
  shiftType: ShiftType;

  // Break tracking
  breakStart: Date | null;
  breakMinutes: number;
  isOnBreak: boolean;

  // Pre-shift checklist
  checklist: ShiftChecklist;

  // Section assignment (FOH)
  assignedSection: Section | null;
  assignedTables: Table[];
  selectedSectionId: string | null;

  // Station assignment (BOH)
  selectedStationId: string | null;

  // Pre-shift data cache
  preShiftData: PreShiftData | null;

  // Live stats during shift
  liveStats: ShiftStats;
  lastStatsUpdate: Date | null;

  // Post-shift data
  postShiftNotes: string;
  postShiftIssues: { category: string; description: string; severity: string }[];

  // Actions
  setStatus: (status: ShiftStatus) => void;
  setShiftType: (shiftType: ShiftType) => void;
  setPreShiftData: (data: PreShiftData) => void;
  updateChecklist: (updates: Partial<ShiftChecklist>) => void;
  selectSection: (sectionId: string) => void;
  selectStation: (stationId: string) => void;
  startShift: (timeEntryId: string, section: Section | null, tables: Table[]) => void;
  updateLiveStats: (stats: ShiftStats) => void;
  setPostShiftNotes: (notes: string) => void;
  addPostShiftIssue: (issue: { category: string; description: string; severity: string }) => void;
  removePostShiftIssue: (index: number) => void;
  endShift: () => void;
  resetShift: () => void;
  startBreak: () => void;
  endBreak: (breakMinutes: number) => void;
  updateBreakState: (breakStart: Date | null, breakMinutes: number) => void;

  // Computed helpers
  isChecklistComplete: (role?: string) => boolean;
  getShiftDuration: () => number; // minutes
  getBreakDuration: () => number; // current break duration in minutes
}

const initialChecklist: ShiftChecklist = {
  menuReviewed: false,
  allergiesReviewed: false,
  sectionConfirmed: false,
  reservationsReviewed: false,
  eightySixReviewed: false,
  stationConfirmed: false,
  overviewReviewed: false,
};

const initialStats: ShiftStats = {
  tablesServed: 0,
  guestsServed: 0,
  salesTotalCents: 0,
  tipsTotalCents: 0,
};

export const useShiftStore = create<ShiftState>()(
  persist(
    (set, get) => ({
      // Initial state
      status: 'no_shift',
      timeEntryId: null,
      shiftStartTime: null,
      shiftType: 'server',
      breakStart: null,
      breakMinutes: 0,
      isOnBreak: false,
      checklist: { ...initialChecklist },
      assignedSection: null,
      assignedTables: [],
      selectedSectionId: null,
      selectedStationId: null,
      preShiftData: null,
      liveStats: { ...initialStats },
      lastStatsUpdate: null,
      postShiftNotes: '',
      postShiftIssues: [],

      // Actions
      setStatus: (status) => set({ status }),

      setShiftType: (shiftType) => set({ shiftType }),

      setPreShiftData: (data) => set({
        preShiftData: data,
        assignedSection: data.assignedSection ? {
          id: data.assignedSection.id,
          name: data.assignedSection.name,
          color: data.assignedSection.color,
        } : null,
        assignedTables: data.assignedSection?.tables || [],
        selectedSectionId: data.assignedSection?.id || null,
        status: 'pre_shift',
      }),

      updateChecklist: (updates) => set((state) => ({
        checklist: { ...state.checklist, ...updates },
      })),

      selectSection: (sectionId) => {
        const state = get();
        const section = state.preShiftData?.availableSections.find(s => s.id === sectionId);
        if (section) {
          set({
            selectedSectionId: sectionId,
            assignedSection: {
              id: section.id,
              name: section.name,
              color: section.color,
            },
            assignedTables: section.tables || [],
          });
        }
      },

      selectStation: (stationId) => {
        set({ selectedStationId: stationId });
      },

      startShift: (timeEntryId, section, tables) => set({
        status: 'active',
        timeEntryId,
        shiftStartTime: new Date(),
        assignedSection: section,
        assignedTables: tables,
        liveStats: { ...initialStats },
      }),

      updateLiveStats: (stats) => set({
        liveStats: stats,
        lastStatsUpdate: new Date(),
      }),

      setPostShiftNotes: (notes) => set({ postShiftNotes: notes }),

      addPostShiftIssue: (issue) => set((state) => ({
        postShiftIssues: [...state.postShiftIssues, issue],
      })),

      removePostShiftIssue: (index) => set((state) => ({
        postShiftIssues: state.postShiftIssues.filter((_, i) => i !== index),
      })),

      endShift: () => set({ status: 'post_shift' }),

      startBreak: () => set({
        breakStart: new Date(),
        isOnBreak: true,
      }),

      endBreak: (breakMinutesFromApi: number) => set({
        breakStart: null,
        breakMinutes: breakMinutesFromApi,
        isOnBreak: false,
      }),

      updateBreakState: (breakStart, breakMinutes) => set({
        breakStart,
        breakMinutes,
        isOnBreak: !!breakStart,
      }),

      resetShift: () => set({
        status: 'no_shift',
        timeEntryId: null,
        shiftStartTime: null,
        shiftType: 'server',
        breakStart: null,
        breakMinutes: 0,
        isOnBreak: false,
        checklist: { ...initialChecklist },
        assignedSection: null,
        assignedTables: [],
        selectedSectionId: null,
        selectedStationId: null,
        preShiftData: null,
        liveStats: { ...initialStats },
        lastStatsUpdate: null,
        postShiftNotes: '',
        postShiftIssues: [],
      }),

      // Computed helpers
      isChecklistComplete: (role?: string) => {
        const { checklist, preShiftData } = get();
        const employeeRole = role || preShiftData?.employee?.primaryRole || 'server';
        const requiredItems = getRequiredChecklistItems(employeeRole);

        // If no required items, checklist is complete
        if (requiredItems.length === 0) return true;

        // Check all required items are completed
        return requiredItems.every((item: ChecklistItem) => checklist[item] === true);
      },

      getShiftDuration: () => {
        const { shiftStartTime, breakMinutes, breakStart } = get();
        if (!shiftStartTime) return 0;
        const totalMinutes = Math.round((Date.now() - new Date(shiftStartTime).getTime()) / 60000);
        // Subtract accumulated break time and current break time
        let currentBreakMinutes = 0;
        if (breakStart) {
          currentBreakMinutes = Math.round((Date.now() - new Date(breakStart).getTime()) / 60000);
        }
        return totalMinutes - breakMinutes - currentBreakMinutes;
      },

      getBreakDuration: () => {
        const { breakStart } = get();
        if (!breakStart) return 0;
        return Math.round((Date.now() - new Date(breakStart).getTime()) / 60000);
      },
    }),
    {
      name: 'mise-pos-shift',
      partialize: (state) => ({
        status: state.status,
        timeEntryId: state.timeEntryId,
        shiftStartTime: state.shiftStartTime,
        shiftType: state.shiftType,
        breakStart: state.breakStart,
        breakMinutes: state.breakMinutes,
        isOnBreak: state.isOnBreak,
        assignedSection: state.assignedSection,
        assignedTables: state.assignedTables,
        selectedStationId: state.selectedStationId,
        liveStats: state.liveStats,
      }),
    }
  )
);
